import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/features/auth/session';
import * as studentsApi from '@/features/students/api';
import type {
  CreateStudentInput,
  StudentView,
  UpdatePreferredMethodInput,
  UpdateStudentInput,
} from '@/features/students/types';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import type { ApiError } from '@/lib/api/client';
import type { PagedData } from '@/lib/api/types';
import { TENANT_QUERY_PREFIX } from '@/lib/api/tenant-query';

/** `GET /students` allows at most `limit=100` per page (server validation). */
const SELF_RESOLUTION_PAGE_SIZE = 100;

/**
 * Pages through the caller's own (server-narrowed) `GET /students` scope
 * looking for the row whose `userId` matches the authenticated user - the
 * only truthful way to answer "which Student record is me", since no
 * endpoint returns it directly and `students[0]` is not a guaranteed
 * shape (a Student who also holds an ACTIVE Guardian link in the same
 * organization has more than one row in scope, in no specified order).
 * Bounded by the scope's own real page count - never assumes a single
 * page is enough, but a narrowed scope is realistically always small.
 */
async function findMyStudentProfile(userId: string): Promise<StudentView | null> {
  let page = 1;
  for (;;) {
    const result = await studentsApi.listStudents(page, SELF_RESOLUTION_PAGE_SIZE);
    const match = result.items.find((item) => item.userId === userId);
    if (match !== undefined) {
      return match;
    }
    // An empty page means there is nothing left to search, regardless of
    // what `total_pages` claims - stops any possibility of looping
    // forever against an inconsistent response.
    if (page >= result.total_pages || result.items.length === 0) {
      return null;
    }
    page += 1;
  }
}

/**
 * Tenant-scoped queries (ADR-026 Addendum, AO-9): the query key embeds
 * the Active Organization id so Org A data cannot structurally satisfy
 * an Org B query, and `enabled` keeps the request from firing at all
 * with no Active Organization selected - matching the same condition
 * the shared HTTP client itself enforces (AO-15).
 */
const studentsListKey = (organizationId: string | null, page: number, limit: number) =>
  [TENANT_QUERY_PREFIX, organizationId, 'students', 'list', page, limit] as const;
const studentDetailKey = (organizationId: string | null, id: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'students', 'detail', id] as const;
const myStudentProfileKey = (organizationId: string | null, userId: string | null) =>
  [TENANT_QUERY_PREFIX, organizationId, 'students', 'me', userId] as const;

export function useStudents(
  page = 1,
  limit = 20,
): UseQueryResult<PagedData<StudentView>, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: studentsListKey(activeOrganizationId, page, limit),
    queryFn: () => studentsApi.listStudents(page, limit),
    enabled: activeOrganizationId !== null,
  });
}

/**
 * Resolves the authenticated user's own Student record in the active
 * organization (Phase G-4, Self-resolution). `enabled` must be the
 * caller's own `students:manage` capability check, inverted - a
 * broad-access caller (Teacher/Admin/Super Admin) is never narrowed to a
 * meaningful "self" scope by `GET /students`, and would otherwise pay for
 * an org-wide fetch that can never resolve to their own row (the schema's
 * `uq_organization_members_org_user` constraint means one role per user
 * per organization, so a Teacher/Admin cannot simultaneously hold the
 * Student role a Student profile requires). Resolves to `null` (not an
 * error) when the caller genuinely has no Student profile in this
 * organization - a Parent-only account, for example.
 */
export function useMyStudentProfile(enabled = true): UseQueryResult<StudentView | null, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  const { user } = useAuth();
  const userId = user?.userId ?? null;
  return useQuery({
    queryKey: myStudentProfileKey(activeOrganizationId, userId),
    queryFn: () => findMyStudentProfile(userId as string),
    enabled: activeOrganizationId !== null && userId !== null && enabled,
  });
}

export function useStudent(id: string): UseQueryResult<StudentView, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: studentDetailKey(activeOrganizationId, id),
    queryFn: () => studentsApi.getStudent(id),
    enabled: activeOrganizationId !== null,
  });
}

export function useCreateStudent(): UseMutationResult<StudentView, ApiError, CreateStudentInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: CreateStudentInput) => studentsApi.createStudent(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'students', 'list'],
      });
    },
  });
}

export function useUpdateStudent(
  id: string,
): UseMutationResult<StudentView, ApiError, UpdateStudentInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: UpdateStudentInput) => studentsApi.updateStudent(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: studentDetailKey(activeOrganizationId, id),
      });
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'students', 'list'],
      });
    },
  });
}

export function useUpdatePreferredMethod(
  id: string,
): UseMutationResult<StudentView, ApiError, UpdatePreferredMethodInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: UpdatePreferredMethodInput) => studentsApi.updatePreferredMethod(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: studentDetailKey(activeOrganizationId, id),
      });
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'students', 'list'],
      });
    },
  });
}

export function useDeactivateStudent(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (id: string) => studentsApi.deactivateStudent(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'students'],
      });
    },
  });
}
